import 'quill/dist/quill.snow.css';
import './quillTextEditor.css';

import Quill from 'quill';
import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useImperativeHandle,
} from 'react';

type EditorProps = {
  readOnly?: boolean;
  defaultValue?: string;
  onTextChange?: (html: string) => void;
  onSelectionChange?: (...args: any[]) => void;
};

export const Editor = forwardRef(({
  readOnly = false,
  defaultValue,
  onTextChange,
  onSelectionChange,
}: EditorProps, ref) => {
  const containerEl = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);

  const initialValueRef = useRef(defaultValue);
  const textChangeCallbackRef = useRef(onTextChange);
  const selectionChangeCallbackRef = useRef(onSelectionChange);

  useLayoutEffect(() => {
    textChangeCallbackRef.current = onTextChange;
    selectionChangeCallbackRef.current = onSelectionChange;
  });

  useImperativeHandle(ref, () => quillRef.current!, [quillRef.current]);

  useEffect(() => {
    quillRef.current?.enable?.(!readOnly);
  }, [readOnly]);

  useEffect(() => {
    const container = containerEl.current;
    if (!container) return;

    const editorRoot = document.createElement('div');
    container.appendChild(editorRoot);

    const quill = new Quill(editorRoot, {
      theme: 'snow',
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['link'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ color: [] }, { background: [] }],
        ],
      },
    });

    quillRef.current = quill;

    if (initialValueRef.current) {
      quill.clipboard.dangerouslyPasteHTML(initialValueRef.current);
    }

    quill.on(Quill.events.TEXT_CHANGE, () => {
      textChangeCallbackRef.current?.(quill.root.innerHTML);
    });

    quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
      selectionChangeCallbackRef.current?.(...args);
    });

    return () => {
      quillRef.current = null;
      container.innerHTML = '';
    };
  }, []);

  return <div id="toolbar-container" ref={containerEl} />;
});

Editor.displayName = 'Editor';

export default Editor;
